import { useState } from 'react'

interface TimePickerProps {
  hours: number
  minutes: number
  onChange: (hours: number, minutes: number) => void
}

function TimePicker({ hours, minutes, onChange }: TimePickerProps) {
  const [isHourMode, setIsHourMode] = useState(true)
  const [hoveredValue, setHoveredValue] = useState<number | null>(null)
  
  // Determine if current hour is AM or PM
  const isPM = hours >= 12

  const radius = 80
  const centerX = 100
  const centerY = 100

  // Calculate position for a number on the clock face
  const getPosition = (value: number, max: number) => {
    const angle = (value / max) * 2 * Math.PI - Math.PI / 2 // Start from top
    const x = centerX + radius * Math.cos(angle)
    const y = centerY + radius * Math.sin(angle)
    return { x, y }
  }

  const handleClick = (value: number) => {
    if (isHourMode) {
      // Convert 12-hour display to 24-hour format
      // value is 1-12 on the clock face
      // Use current isPM state to determine if it's AM or PM
      let newHour: number
      if (value === 12) {
        // 12 on clock = 0 (midnight) or 12 (noon) in 24-hour
        newHour = isPM ? 12 : 0
      } else {
        // 1-11 on clock = 1-11 (AM) or 13-23 (PM) in 24-hour
        newHour = isPM ? value + 12 : value
      }
      
      onChange(newHour, minutes)
      setIsHourMode(false)
    } else {
      onChange(hours, value)
      setIsHourMode(true)
    }
  }
  
  const toggleAMPM = () => {
    let newHour: number
    if (hours === 0) {
      // Midnight (0) -> Noon (12)
      newHour = 12
    } else if (hours === 12) {
      // Noon (12) -> Midnight (0)
      newHour = 0
    } else if (hours < 12) {
      // AM (1-11) -> PM (13-23)
      newHour = hours + 12
    } else {
      // PM (13-23) -> AM (1-11)
      newHour = hours - 12
    }
    onChange(newHour, minutes)
  }

  const handleCenterClick = () => {
    setIsHourMode(!isHourMode)
  }

  // Generate numbers for the clock
  const numbers = isHourMode 
    ? Array.from({ length: 12 }, (_, i) => i + 1) // 1-12 for hours
    : Array.from({ length: 60 }, (_, i) => i).filter(n => n % 5 === 0) // 0, 5, 10, ... 55 for minutes

  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours
  const displayMinutes = minutes

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: '200px', height: '200px' }}>
        {/* Clock face */}
        <svg width="200" height="200" className="absolute inset-0">
          {/* Outer circle */}
          <circle
            cx={centerX}
            cy={centerY}
            r={radius}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth="2"
          />
          
          {/* Hour/minute hand */}
          {(() => {
            const value = isHourMode ? displayHours : displayMinutes
            const max = isHourMode ? 12 : 60
            const angle = (value / max) * 2 * Math.PI - Math.PI / 2
            const handLength = isHourMode ? radius * 0.6 : radius * 0.7
            const endX = centerX + handLength * Math.cos(angle)
            const endY = centerY + handLength * Math.sin(angle)
            
            return (
              <line
                x1={centerX}
                y1={centerY}
                x2={endX}
                y2={endY}
                stroke={isHourMode ? "#3b82f6" : "#10b981"}
                strokeWidth="3"
                strokeLinecap="round"
              />
            )
          })()}
          
          {/* Center dot */}
          <circle
            cx={centerX}
            cy={centerY}
            r="6"
            fill={isHourMode ? "#3b82f6" : "#10b981"}
            className="cursor-pointer"
            onClick={handleCenterClick}
          />
        </svg>

        {/* Number buttons */}
        {numbers.map((num) => {
          const pos = getPosition(num, isHourMode ? 12 : 60)
          const isSelected = isHourMode 
            ? (num === displayHours || (num === 12 && hours === 0))
            : num === displayMinutes
          const isHovered = hoveredValue === num
          
          return (
            <button
              key={num}
              type="button"
              onClick={() => handleClick(isHourMode ? (num === 12 ? 0 : num) : num)}
              onMouseEnter={() => setHoveredValue(num)}
              onMouseLeave={() => setHoveredValue(null)}
              className={`absolute w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                isSelected
                  ? 'bg-blue-600 text-white scale-110'
                  : isHovered
                  ? 'bg-blue-100 text-blue-700 scale-105'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
              style={{
                left: `${pos.x - 16}px`,
                top: `${pos.y - 16}px`,
                transform: 'translate(0, 0)',
              }}
            >
              {num}
            </button>
          )
        })}
      </div>

      {/* Mode indicator and time display */}
      <div className="mt-4 flex flex-col items-center gap-2">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setIsHourMode(true)}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
              isHourMode
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Hour
          </button>
          <button
            type="button"
            onClick={() => setIsHourMode(false)}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
              !isHourMode
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Minute
          </button>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-lg font-semibold text-gray-900">
            {String(displayHours).padStart(2, '0')}:{String(minutes).padStart(2, '0')}
          </div>
          <button
            type="button"
            onClick={toggleAMPM}
            className="px-3 py-1 rounded text-sm font-medium transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            {isPM ? 'PM' : 'AM'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default TimePicker
